import { uuid } from "@xapp/chat-widget-core";
import {
    ChannelActionRequest,
    IntentRequest,
    LaunchRequest,
    OptionSelectRequest,
    PermissionRequest,
    Response,
} from "stentor-models";
import { StentorApiConfig } from "../config";
import { WidgetEvent, WidgetEventsRequest, WidgetEventsResponse } from "../types/events";
import { err, log } from "../utils";
import { addTestLeadAttribute, ATTRIBUTION_PARAMETERS } from "@xapp/chat-widget-core";

export type StentorRequest =
    | IntentRequest
    | LaunchRequest
    | OptionSelectRequest
    | PermissionRequest
    | ChannelActionRequest;

const delay = (waitTimeInMs: number) =>
    new Promise((resolve) => {
        setTimeout(resolve, waitTimeInMs);
    });

const XAPP_USER_ID = "__xapp_form_user_id";

/**
 * Marks a response for a channel action that was suppressed because a final submit had
 * already gone out on this session, rather than one that was actually sent.
 */
const DROPPED_AFTER_FINAL = "__xappDroppedAfterFinal";

/**
 * True when the response came from the post-final suppression path rather than the
 * server. Callers use it to tell "we deliberately did not send this" apart from "the
 * server returned nothing" -- the two were indistinguishable before issue #1373.
 */
export function wasDroppedAfterFinal(response: unknown): boolean {
    return !!response && (response as Record<string, unknown>)[DROPPED_AFTER_FINAL] === true;
}

// One cached StentorApi per distinct widget config. A page can host multiple form widgets, each
// bound to a different handler via its own intentId (see issue #1482), and each is an independent
// conversation with its own session — so a single process-wide instance would make the second
// widget send the first's intentId as overrideKey. Keyed by the fields that define a widget's
// identity; the Form/FormWidget pair within one tree passes matching config and so shares one entry.
const API_INSTANCES = new Map<string, StentorApi>();

const apiCacheKey = (config: StentorApiConfig): string =>
    // demoMode is part of the identity, not just behaviour: a page can host a
    // live widget and a preview of the same handler, and sharing one instance
    // between them would let the preview submit through the live instance.
    `${config.url} ${config.key} ${config.intentId ?? ""} ${config.demoMode ? "demo" : "live"}`;

/**
 * The only request types demo mode lets through.
 *
 * An **allowlist**, deliberately. A denylist of write types would let any
 * request type added later — OPTION_SELECT and PERMISSION_REQUEST already
 * exist unused in the StentorRequest union — through by default, and the
 * guarantee would quietly weaken as the request surface grows. Failing closed
 * means a new type is blocked until someone decides it is a read.
 *
 * These two only fetch the form definition; without them there is no form to
 * preview at all. Everything else, CHANNEL_ACTION_REQUEST above all, can
 * create a lead, notify the business, or land in reporting.
 *
 * So the guarantee is precisely "a preview cannot create a lead or a tracked
 * event", not "a preview makes no requests".
 */
const DEMO_READ_REQUEST_TYPES = new Set<string>(["LAUNCH_REQUEST", "INTENT_REQUEST"]);

const RESPONSE_TTL_MS = 900 * 1000; // 15 minutes

// Cached tracking parameters to avoid repeated localStorage access
let cachedTrackingParams: Record<string, string> | null = null;
let cacheTimestamp: number = 0;
const TRACKING_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Gets tracking parameters with memory caching to reduce localStorage access
 */
function getCachedTrackingParameters(): Record<string, string> {
    const now = Date.now();

    // Return cached parameters if still valid
    if (cachedTrackingParams && now - cacheTimestamp < TRACKING_CACHE_DURATION) {
        return cachedTrackingParams;
    }

    // Refresh cache
    const attributes: Record<string, string> = {};

    // Sourced from chat-widget-core rather than restated. tracking.ts persists these
    // keys and this reads them back; when the two lists were maintained separately the
    // read-back silently lagged, so a persisted value never reached the server and
    // looked exactly like a visitor who arrived with no attribution at all.
    const trackingParameterKeys = ATTRIBUTION_PARAMETERS;

    // Add UTM and click ID parameters from localStorage
    trackingParameterKeys.forEach((key) => {
        const storedValue = localStorage.getItem(`xa_tracking_${key}`);
        if (storedValue) {
            try {
                const data = JSON.parse(storedValue);
                if (data.expires > now) {
                    attributes[key] = data.value;
                }
            } catch (e) {
                localStorage.removeItem(`xa_tracking_${key}`);
            }
        }
    });

    // Add custom tracking parameters (track_* prefix)
    // Use localStorage.length and key() for proper iteration across all environments
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("xa_tracking_track_")) {
            try {
                const data = JSON.parse(localStorage.getItem(key) || "{}");
                if (data.expires > now) {
                    const paramName = key.replace("xa_tracking_", "");
                    attributes[paramName] = data.value;
                }
            } catch (e) {
                localStorage.removeItem(key);
            }
        }
    }

    // Update cache
    cachedTrackingParams = attributes;
    cacheTimestamp = now;

    return attributes;
}

export class StentorApi {
    private responseCache: {
        [key: string]: {
            ts: number;
            value: Response;
        };
    };

    private readonly config: StentorApiConfig;
    private session: StentorApiSession | undefined;

    private prefetchSemaphore = Promise.resolve();

    private constructor(config: StentorApiConfig) {
        this.config = config;
        this.responseCache = {};
    }

    /**
     * Returns the cached API instance for this widget config, creating it on first use.
     *
     * Instances are keyed by url + key + intentId, so two widgets on one page with different
     * intentIds each get their own instance (and their own session) instead of colliding on a
     * single process-wide one. The Form/FormWidget pair within a single widget tree passes matching
     * config and so shares one entry.
     */
    public static getApi(config: StentorApiConfig): StentorApi {
        const cacheKey = apiCacheKey(config);
        const existing = API_INSTANCES.get(cacheKey);
        if (existing) {
            return existing;
        }

        const api = new StentorApi(config);
        API_INSTANCES.set(cacheKey, api);

        if (config.prefetchQuery) {
            api.prefetch(config.prefetchQuery);
        }

        return api;
    }

    public launchRequest(query: string): Promise<Response> {
        return this.send({
            type: "LAUNCH_REQUEST",
            rawQuery: query,
            intentId: "LaunchRequest",
            platform: "stentor-platform",
            channel: "form-widget",
        } as StentorRequest);
    }

    public intentRequest(request: { query?: string; intentId?: string }): Promise<Response> {
        // if we have the intentId, use that and set rawQuery to be undefined
        // so that stentor doesn't try to resolve it again
        // otherwise, use the query and set intentId to be "NLU_RESULT_PLACEHOLDER"
        const rawQuery: string | undefined = request?.intentId ? undefined : request.query;
        const intentId: string = request?.intentId ? request.intentId : "NLU_RESULT_PLACEHOLDER";

        return this.send({
            type: "INTENT_REQUEST",
            rawQuery,
            intentId,
            platform: "stentor-platform",
            channel: "form-widget",
        } as StentorRequest);
    }

    public channelActionRequest(data: unknown, action: string): Promise<Response> {
        const isFinal = !!(data as any).final;

        if (this.session) {
            if (this.session.hadFinal) {
                log(`Had final submit. Not sending action request: ${action}`);
                // Flagged rather than a bare {}: suppressing a duplicate after the final
                // submit is correct, but an indistinguishable empty response meant a
                // caller could not tell a dropped submit from a delivered one -- so a
                // wrongly-latched session looked exactly like success (issue #1373).
                // A plain string key, not a Symbol: the module is re-required per test
                // and Symbol identity would not survive that.
                return Promise.resolve({ [DROPPED_AFTER_FINAL]: true } as unknown as Response);
            }

            this.session.hadFinal = isFinal;
        }

        return this.send({
            type: "CHANNEL_ACTION_REQUEST",
            action,
            platform: "stentor-platform",
            channel: "form-widget",
            ...(this.config.intentId ? { overrideKey: this.config.intentId } : {}),
            attributes: {
                data,
            },
            // The double cast is required: ChannelActionRequest requires userId, which send() fills
            // in below. It is NOT about overrideKey — that is a declared optional field on the
            // request type. A single `as StentorRequest` does not compile here.
        } as unknown as StentorRequest).catch((error) => {
            // The latch goes up synchronously above so a double-click cannot put two
            // finals in flight. But a final that never reached the server must not keep
            // it up: leaving it latched turns every retry into an instant
            // DROPPED_AFTER_FINAL "success", which is precisely how a lead goes missing
            // (issue #1373). Unlatch and let the caller retry for real.
            if (this.session && isFinal) {
                this.session.hadFinal = false;
            }
            throw error;
        });
    }

    /**
     * Track form widget interaction events
     * Sends events to POST /cms/widget/events
     *
     * Authentication uses the widget key in the request body (not headers).
     *
     * @param eventType - Type of event (next_step, submit, partial_data)
     * @param event - The widget event to track
     * @returns The response with processed count, or undefined on error
     */
    public async trackEvent(
        eventType: string,
        event: WidgetEvent
    ): Promise<WidgetEventsResponse | undefined> {
        try {
            const { key } = this.config;
            if (this.config.demoMode) {
                // A preview interaction must not show up in funnel analytics as
                // a real form open/submit.
                log("[demoMode] suppressed tracking event", eventType);
                return undefined;
            }
            if (!key) {
                err("[EventTracking] No widget key configured, skipping event tracking");
                return undefined;
            }
            const trackingUrl = this.getTrackingUrl();

            // Request body includes key for authentication (no Authorization header needed)
            const requestBody: WidgetEventsRequest = {
                key,
                events: [event],
            };

            const params: RequestInit = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
                mode: "cors",
            };

            const response = await fetch(trackingUrl, params);

            if (!response.ok) {
                err(`Event tracking failed: ${response.status} ${response.statusText}`);
                // Don't throw - we don't want to block user flow
                return undefined;
            }

            const result: WidgetEventsResponse = await response.json();
            log(`Event tracking succeeded: ${eventType}, processed: ${result.processed}`);
            return result;
        } catch (error) {
            // Log error but don't throw - events should never block user flow
            err(`Event tracking error: ${error}`);
            return undefined;
        }
    }

    /**
     * Track form widget interaction events for page unload scenarios.
     *
     * Uses fetch with keepalive instead of sendBeacon because:
     * - sendBeacon with Content-Type: application/json triggers CORS preflight
     * - sendBeacon doesn't properly wait for preflight response before firing
     * - fetch with keepalive handles the preflight handshake correctly
     *
     * @param event - The widget event to track
     * @returns true if the request was queued (not necessarily delivered), false if queuing failed
     */
    public trackEventBeacon(event: WidgetEvent): boolean {
        const { key } = this.config;
        if (this.config.demoMode) {
            // The beacon fires on beforeunload and on the midnight timeout, and
            // its payload is `partialData` — the values the visitor actually
            // typed. A preview that gets abandoned would otherwise post real
            // form data to the events endpoint, which is exactly what demo mode
            // exists to prevent.
            log("[demoMode] suppressed tracking beacon", event.eventType);
            return false;
        }
        if (!key) {
            err("[EventTracking] No widget key configured, skipping event beacon");
            return false;
        }

        try {
            const trackingUrl = this.getTrackingUrl();
            const requestBody: WidgetEventsRequest = {
                key,
                events: [event],
            };

            // Use fetch with keepalive for proper CORS handling during page unload.
            // keepalive allows the request to outlive the page.
            // Note: This is fire-and-forget - we return true once queued, regardless of
            // eventual success/failure, since the page may be gone by the time it completes.
            fetch(trackingUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
                keepalive: true,
            }).catch(() => {
                // Silently ignore errors - page may already be unloaded
            });

            return true;
        } catch (error) {
            err(`Event beacon error: ${error}`);
            return false;
        }
    }

    /**
     * Build the tracking endpoint URL: /cms/widget/events
     */
    private getTrackingUrl(): string {
        const { url } = this.config;

        try {
            const parsedUrl = new URL(url);
            // Extract origin (protocol + host) and build the tracking endpoint
            return `${parsedUrl.origin}/cms/widget/events`;
        } catch (e) {
            // Fallback for invalid URLs - try to extract origin manually
            err(`[EventTracking] Failed to parse URL: ${url}`);
            const match = url.match(/^(https?:\/\/[^/]+)/);
            if (match) {
                return `${match[1]}/cms/widget/events`;
            }
            // Last resort: append to the URL
            return `${url.replace(/\/$/, "")}/cms/widget/events`;
        }
    }

    public resetSession(): void {
        this.session = undefined;
    }

    private getCachedResponse(query: string | undefined): Response | undefined {
        if (!query) {
            return;
        }

        const now = new Date().getTime();
        const hit = this.responseCache[query];

        if (hit) {
            if (now - hit.ts < RESPONSE_TTL_MS) {
                log(`Returning cached response for "${query}"`);
                return hit.value;
            }

            log(`Deleting expired cached response for "${query}"`);
            delete this.responseCache[query];
        }

        return;
    }

    private async send(
        data: Omit<StentorRequest, "sessionId" | "userId" | "isNewSession">
    ): Promise<Response> {
        await this.prefetchSemaphore;

        if (this.config.demoMode && !DEMO_READ_REQUEST_TYPES.has(data.type)) {
            // Only reads are allowed through: the form's entire step structure
            // arrives on the INTENT_REQUEST response and is stashed in
            // formDisplayRef, and that handler applies the response
            // unconditionally. Blocking reads too would clobber state with an
            // empty response and render a form with no steps — the opposite of
            // "behaves exactly like production".
            log("[demoMode] suppressed request", data.type);
            // Deliberately partial. Every field on Response that callers read
            // — outputSpeech, displays, context — is optional, and they all
            // guard before use, so absent means "nothing to apply" rather than
            // a missing value. The cast is what makes that intent explicit; if
            // a caller ever starts requiring a field, this is where to add it.
            return {} as Response;
        }

        const hit = this.getCachedResponse(data.rawQuery);
        if (hit) {
            return Promise.resolve(hit);
        }

        const { url, key } = this.config;

        const newSession = !this.session;

        if (!this.session) {
            let storedUserId = localStorage.getItem(XAPP_USER_ID);

            if (!storedUserId) {
                storedUserId = uuid();
                localStorage.setItem(XAPP_USER_ID, storedUserId);
            }

            this.session = {
                sessionId: `stentor-form-session-${uuid()}`,
                userId: storedUserId,
                hadFinal: false,
            };
        }

        const attributes: Record<string, unknown> = {};

        // Add current URL for analytics (exclude localhost for privacy)
        try {
            const href = new URL(window.location.href);
            if (href?.host.toLowerCase().startsWith("localhost")) {
                // Indicate localhost without exposing the full URL
                attributes["isLocal"] = true;
            } else {
                // Not localhost, include the full URL
                attributes["currentUrl"] = window.location.href;
            }
        } catch (e) {
            // If URL parsing fails, skip currentUrl
        }

        // look for certain attributes on the browser and add them.
        // get xa_rwg_token from local storage
        const rwgToken = localStorage.getItem("xa_rwg_token");
        if (rwgToken) {
            attributes["rwg_token"] = rwgToken;
        }
        const merchantId = localStorage.getItem("xa_merchant_id");
        if (merchantId) {
            attributes["merchant_id"] = merchantId;
        }
        const environment = localStorage.getItem("xa_environment");
        if (environment) {
            attributes["environment"] = environment;
        }
        const enablePreferredTime = localStorage.getItem("xa_enablePreferredTime");
        if (enablePreferredTime) {
            attributes["enablePreferredTime"] = enablePreferredTime;
        }
        const service = localStorage.getItem("xa_service");
        if (service) {
            attributes["service"] = service;
        }
        const origin = localStorage.getItem("xa_origin");
        if (origin) {
            attributes["origin"] = origin;
        }

        // Read placeId from URL parameters on every request (not cached like UTM params).
        // This is intentional: in multi-location / SPA scenarios (e.g. Reserve with Google),
        // the URL may change between requests within a session and the routing must always
        // reflect the current placeId. The value is forwarded to the backend as-is; the
        // backend is responsible for validating and using the value safely.
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const placeId = urlParams.get("placeId");
            if (placeId) {
                attributes["placeId"] = placeId;
            }
        } catch (e) {
            // If URL parsing fails, skip placeId
        }

        // Extract tracking parameters using cached approach for better performance
        const trackingParams = getCachedTrackingParameters();
        Object.assign(attributes, trackingParams);

        // A test lead from Studio's "send yourself a test lead" booking page
        addTestLeadAttribute(attributes);

        // Add current time for accurate date/time responses
        attributes["currentTime"] = new Date().toISOString();

        const { sessionId, userId } = this.session;
        const request = {
            ...data,
            sessionId,
            userId,
            isNewSession: newSession,
            attributes: {
                ...data.attributes,
                ...attributes,
            },
        } as StentorRequest;
        const body = JSON.stringify(request);

        const params: RequestInit = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${key}`,
            },
            body,
            mode: "cors",
        };

        let response = await fetch(url, params);

        if (!response.ok) {
            err(`Stentor call failed: ${response.statusText}`);

            await delay(1000);
            response = await fetch(url, params); // once more
            if (!response.ok) {
                err(`Stentor call failed again: ${response.statusText}`);
                throw new Error(`Status ${response.status}, Text: ${response.statusText}`);
            }
        }

        return response.json();
    }

    private async prefetch(query: string): Promise<void> {
        if (this.config.demoMode) {
            return;
        }

        const hit = this.getCachedResponse(query);
        if (hit) {
            return;
        }

        let resolver: (value: void | PromiseLike<void>) => void;

        this.prefetchSemaphore = new Promise((resolve) => {
            resolver = resolve;
        });

        const { url, key } = this.config;

        const newSession = !this.session;

        if (!this.session) {
            let storedUserId = localStorage.getItem(XAPP_USER_ID);

            if (!storedUserId) {
                storedUserId = uuid();
                localStorage.setItem(XAPP_USER_ID, storedUserId);
            }

            this.session = {
                sessionId: `stentor-form-session-${uuid()}`,
                userId: storedUserId,
                hadFinal: false,
            };
        }

        const { sessionId, userId } = this.session;

        const data = {
            type: "INTENT_REQUEST",
            rawQuery: query,
            intentId: "NLU_RESULT_PLACEHOLDER",
            platform: "stentor-platform",
            channel: "form-widget",
        };

        const attributes: Record<string, unknown> = {
            currentTime: new Date().toISOString(),
        };

        const request = {
            ...data,
            sessionId,
            userId,
            isNewSession: newSession,
            attributes,
        } as StentorRequest;

        const body = JSON.stringify(request);

        const params: RequestInit = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${key}`,
            },
            body,
            mode: "cors",
        };

        const start = new Date().getTime();

        fetch(url, params)
            .then(async (fetchResponse) => {
                if (!fetchResponse.ok) {
                    err(`Stentor prefetch failed: ${fetchResponse.statusText}`);
                    return;
                }

                const now = new Date().getTime();

                this.responseCache[query] = {
                    ts: now,
                    value: await fetchResponse.json(),
                };

                log(`Stentor prefetch took: ${now - start} ms`);
            })
            .finally(() => {
                resolver();
            });

        return;
    }
}

export interface StentorApiSession {
    readonly sessionId: string;
    readonly userId: string;
    hadFinal: boolean;
}
